import { ValidatedOptions } from '@patternfly/react-core';
import { debounce } from 'lodash';
import { useState } from 'react';
import {
  fetchCheckDedicatedClusterDeploymentNameAvailable,
  fetchCheckDeploymentNameAvailable,
} from 'src/query/deployments';

export function useValidDeploymentName() {
  const [nameValid, setNameValid] = useState<true | false | null | 'checking'>(null);

  const clearNameValid = () => setNameValid(null);

  const nameValidityResponseText = () => {
    let isNameValid = ValidatedOptions.default;
    let nameHelperText = 'Name must be unique.';
    if (nameValid === false) {
      isNameValid = ValidatedOptions.error;
      nameHelperText = 'Deployment name is not available for use. Please try another.';
    } else if (nameValid) {
      isNameValid = ValidatedOptions.success;
      nameHelperText = 'Deployment name is available for use.';
    }
    if (nameValid === 'checking') {
      nameHelperText = 'Checking for deployment name availability...';
    }

    return {
      isNameValid,
      nameHelperText,
    };
  };

  const checkSharedClusterDeploymentNameAvailability = debounce(
    async (nameToCheck: string) => {
      // if more than 2 characters then make API call to check for unique name
      if (nameToCheck.length > 2) {
        setNameValid('checking');

        try {
          const checkValue = await fetchCheckDeploymentNameAvailable({
            pathParams: { realm: 'self' },
            queryParams: { name: nameToCheck },
          });
          return setNameValid(checkValue.available);
        } catch (e) {
          console.log(e);
        }
      }
      setNameValid(null);
    },
    300,
    {
      maxWait: 1000,
    }
  );

  const checkDedicatedClusterDeploymentNameAvailability = debounce(
    async (nameToCheck: string, clusterId: string) => {
      // if more than 2 characters then make API call to check for unique name
      if (nameToCheck.length > 2) {
        setNameValid('checking');

        try {
          const checkValue = await fetchCheckDedicatedClusterDeploymentNameAvailable({
            pathParams: { realm: 'self', clusterId },
            queryParams: { name: nameToCheck },
          });
          return setNameValid(checkValue.available);
        } catch (e) {
          console.log(e);
        }
      }
      setNameValid(null);
    },
    300,
    {
      maxWait: 1000,
    }
  );

  return {
    checkSharedClusterDeploymentNameAvailability,
    checkDedicatedClusterDeploymentNameAvailability,
    nameValidityResponseText,
    clearNameValid,
  };
}
